import { Button, Checkbox, FloatingIndicator, Modal, Stack, Tabs, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconSearch } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import useCreateEntityLink from 'src/Components/EntityLinks/hooks/useCreateEntityLink';
import useSearchLinkableEntities from 'src/Components/EntityLinks/hooks/useSearchLinkableEntities';
import { ENTITY_TYPE_ORDER } from 'src/Components/EntityLinks/utils/entityLinkConstants';
import { getGroupIcon } from 'src/Components/EntityLinks/utils/entityLinkIcons';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import {
  trackEntityCreateAndLinkClicked,
  trackEntityLinkCreated,
  trackEntityLinkModalOpened,
} from 'src/Services/Mixpanel/events';

import styles from './LinkEntityModal.module.scss';
import { Props } from './LinkEntityModal.types';

// Rooms, events and memories are created from their own module, not from this modal.
const CREATE_ROUTE_BY_TYPE: Partial<Record<LinkableEntityType, string>> = {
  [LinkableEntityType.Task]: 'tasks/new',
  [LinkableEntityType.Project]: 'project-and-works/new',
  [LinkableEntityType.Contact]: 'contacts/new',
  [LinkableEntityType.Expense]: 'expenses/new',
};

interface SelectedEntity {
  type: LinkableEntityType;
  id: string;
  title: string;
}

const selectionKey = (type: LinkableEntityType, id: string): string => `${type}::${id}`;

const LinkEntityModal = ({
  opened,
  onClose,
  sourceType,
  sourceId,
  initialTargetType,
  excludeIdsByType,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  const isMobile = useIsMobile();
  const { mutate: createLink } = useCreateEntityLink();

  const availableTypes = useMemo(
    () =>
      ENTITY_TYPE_ORDER.filter(
        type => type !== sourceType && type !== LinkableEntityType.VirtualFile
      ),
    [sourceType]
  );

  const [activeTab, setActiveTab] = useState<LinkableEntityType>(initialTargetType);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(searchQuery, 300);
  const [selection, setSelection] = useState<Map<string, SelectedEntity>>(new Map());
  const [isLinking, setIsLinking] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [tabsListRef, setTabsListRef] = useState<HTMLDivElement | null>(null);
  const [tabControlRefs, setTabControlRefs] = useState<Record<string, HTMLButtonElement | null>>(
    {}
  );
  const setTabControlRef = (type: LinkableEntityType) => (node: HTMLButtonElement) => {
    tabControlRefs[type] = node;
    setTabControlRefs(tabControlRefs);
  };

  useEffect(() => {
    if (opened) {
      trackEntityLinkModalOpened({
        source_entity_type: sourceType,
        initial_target_type: initialTargetType,
      });
    }
    // Only track the transition to opened, not every prop identity change while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const { results, isLoading } = useSearchLinkableEntities(
    activeTab,
    debouncedQuery,
    excludeIdsByType[activeTab] ?? []
  );

  const toggleSelection = (result: { id: string; title: string }) => {
    const key = selectionKey(activeTab, result.id);
    setSelection(prev => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, { type: activeTab, id: result.id, title: result.title });
      return next;
    });
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelection(new Map());
    setProgress(null);
    onClose();
  };

  const handleLinkSelection = async () => {
    const items = [...selection.values()];
    setIsLinking(true);
    setProgress({ current: 0, total: items.length });

    let failureCount = 0;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      await new Promise<void>(resolve => {
        createLink(
          { sourceType, sourceId, targetType: item.type, targetId: item.id },
          {
            onSuccess: () => {
              trackEntityLinkCreated({
                source_entity_type: sourceType,
                target_entity_type: item.type,
                batch_size: items.length,
              });
              resolve();
            },
            onError: () => {
              failureCount += 1;
              notifications.show({
                message: t('entityLinks.linkError', 'Impossible de lier « {{title}} ».', {
                  title: item.title,
                }),
                color: 'red',
              });
              resolve();
            },
          }
        );
      });
      setProgress({ current: i + 1, total: items.length });
    }

    setIsLinking(false);
    if (failureCount < items.length) {
      notifications.show({
        message: t('entityLinks.linkSuccess', 'Éléments liés.'),
        color: 'teal',
      });
    }
    handleClose();
  };

  const handleCreateNew = () => {
    const route = CREATE_ROUTE_BY_TYPE[activeTab];
    if (!assetId || !route) return;
    trackEntityCreateAndLinkClicked({ source_entity_type: sourceType, new_entity_type: activeTab });
    handleClose();
    navigate(`/asset/${assetId}/${route}?linkSourceType=${sourceType}&linkSourceId=${sourceId}`);
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t('entityLinks.modalTitle', 'Lier des éléments à cette fiche')}
      size="lg"
      fullScreen={isMobile}
    >
      <Tabs
        value={activeTab}
        onChange={value => value && setActiveTab(value as LinkableEntityType)}
        variant="unstyled"
        className={styles.tabs}
      >
        <Tabs.List ref={setTabsListRef} className={styles.tabsList}>
          {availableTypes.map(type => {
            const Icon = getGroupIcon(type);
            return (
              <Tabs.Tab
                key={type}
                value={type}
                ref={setTabControlRef(type)}
                leftSection={<Icon size={14} />}
              >
                {t(`entityLinks.groups.${type}.plural`, type)}
              </Tabs.Tab>
            );
          })}
          <FloatingIndicator
            target={tabControlRefs[activeTab]}
            parent={tabsListRef}
            className={styles.indicator}
          />
        </Tabs.List>

        {availableTypes.map(type => (
          <Tabs.Panel key={type} value={type} className={styles.panel}>
            <TextInput
              placeholder={t('entityLinks.searchPlaceholder', 'Rechercher...')}
              leftSection={<IconSearch size={16} />}
              value={type === activeTab ? searchQuery : ''}
              onChange={e => setSearchQuery(e.currentTarget.value)}
            />
            <Stack gap="xs" className={styles.results}>
              {type === activeTab && isLoading && (
                <div className={styles.empty}>{t('common.loading', 'Chargement...')}</div>
              )}
              {type === activeTab && !isLoading && results.length === 0 && (
                <div className={styles.empty}>{t('entityLinks.noResults', 'Aucun résultat')}</div>
              )}
              {type === activeTab &&
                results.map(result => (
                  <Checkbox
                    key={result.id}
                    label={result.subtitle ? `${result.title} — ${result.subtitle}` : result.title}
                    checked={selection.has(selectionKey(activeTab, result.id))}
                    onChange={() => toggleSelection(result)}
                  />
                ))}
            </Stack>
            {CREATE_ROUTE_BY_TYPE[type] && (
              <Button variant="light" fullWidth onClick={handleCreateNew}>
                {t('entityLinks.createNew', 'Créer une nouvelle {{type}}', {
                  type: t(`entityLinks.groups.${type}.singular`, type).toLowerCase(),
                })}
              </Button>
            )}
          </Tabs.Panel>
        ))}
      </Tabs>

      <div className={styles.footer}>
        <Button variant="outline" onClick={handleClose}>
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button onClick={handleLinkSelection} disabled={selection.size === 0} loading={isLinking}>
          {isLinking && progress
            ? t('entityLinks.linkingProgress', 'Lien {{current}}/{{total}}...', progress)
            : /* Pluralized key (linkSelection_one/_other) — i18next's count-based plural
                 resolution doesn't combine with this codebase's usual (key, default, opts)
                 call shape, so no inline default string here; see translation.json. */
              t('entityLinks.linkSelection', { count: selection.size })}
        </Button>
      </div>
    </Modal>
  );
};

export default LinkEntityModal;
